import { defaultConditionUids } from "js/includes/editors/Policy/PolicyEditor/tabs/Conditions/defaults"
import { defaultConditionUids as defaultAgentConditionUids } from "js/includes/editors/Policy/Models/Conditions/DefaultConditions"
import { localized } from "js/includes/common/utils"
import { flip, divide, multiply, pick, isEmpty, map, reduce } from "ramda"
import {
  operatorOptionsKeys,
  conditionAbrevToWord,
  stateKeysToObject,
  modeKeysToObject,
  unitBpsOptionsKeys,
  rwOptionsKeys,
  byteOptionsKeys,
} from "js/includes/editors/Policy/PolicyEditor/tabs/Conditions/conditionData"
import { ResetThresholdOptionsWithoutManual } from "js/includes/common/_options"
import { convertBytes } from "js/includes/common/utils"

export const valueFromSeconds = flip(divide)(3600)
export const valueToSeconds = multiply(3600)

const getLocalizationStringFor = conditionUid => {
  switch (conditionUid) {
    case defaultConditionUids.CPU:
      return "editors.policy.conditions.aggregateCpuUsage"
    case defaultConditionUids.Memory:
      return "editors.policy.conditions.memoryUsage"
    case defaultConditionUids.DiskUsage:
    case defaultAgentConditionUids.DiskUsage:
      return "editors.policy.conditions.diskUsage"
    case defaultAgentConditionUids.DiskFreeSpace:
      return "editors.policy.conditions.diskFree"
    case defaultConditionUids.DataStoreFreeSpace:
      return "editors.policy.conditions.dataStoreFreeSpace"
    case defaultConditionUids.BadSensorCount:
      return "editors.policy.conditions.badSensorCount"
    case defaultConditionUids.DeviceDown:
    case defaultAgentConditionUids.DeviceDown:
      return "editors.policy.conditions.deviceDown"
    case defaultConditionUids.SensorHealth:
      return "editors.policy.conditions.sensorHealth"
    case defaultConditionUids.ProcessorUsage:
      return "editors.policy.conditions.processorUsage"
    case defaultConditionUids.GuestOperationMode:
      return "editors.policy.conditions.guestOperationMode"
    case defaultConditionUids.SnapshotSize:
      return "editors.policy.conditions.snapshotSize"
    case defaultConditionUids.SnapshotLifeSpan:
      return "editors.policy.conditions.snapshotLifeSpan"
    case defaultConditionUids.CheckpointSize:
      return "editors.policy.conditions.checkpointSize"
    case defaultConditionUids.CheckpointLifeSpan:
      return "editors.policy.conditions.checkpointLifeSpan"
    case defaultConditionUids.GuestToolsNotRunning:
      return "editors.policy.conditions.guestToolsNotRunning"
    case defaultAgentConditionUids.NetworkUtilization:
      return "editors.policy.conditions.networkUtil"
    default:
      throw new Error("Invalid")
  }
}

const getUnitType = unit => {
  switch (unit) {
    case "MINUTE":
      return "general.minuteOrMinutes"
    case "HOUR":
      return "general.hourOrHours"
    case "DAY":
      return "editors.policy.conditions.days"
    case "WEEK":
      return "general.weeks"
    case "MONTH":
      return "general.months"
    default:
      throw new Error("Invalid")
  }
}

const getStatusString = (state, keysToObject) => {
  return reduce(
    (a, b) => {
      return a + b.label + ", "
    },
    "",
    arrayFromKeys(state, keysToObject),
  ).slice(0, -2)
}

export const generateConditionName = (parameters, conditionUid) => {
  const { unit } = parameters
  var name = ""
  var conditionName = ""

  switch (conditionUid) {
    case defaultConditionUids.CPU:
      return (
        localized(getLocalizationStringFor(conditionUid)) +
        " " +
        localized("editors.policy.conditions.is") +
        " " +
        operatorOptionsKeys[parameters.operator]() +
        " " +
        parameters.threshold +
        " % " +
        localized("editors.policy.conditions.for") +
        " " +
        parameters.duration +
        " " +
        localized("general.minutes").toLowerCase()
      )
    case defaultConditionUids.Memory:
    case defaultConditionUids.DiskUsage:
    case defaultAgentConditionUids.DiskUsage:
    case defaultAgentConditionUids.MacDiskUsage:
    case defaultAgentConditionUids.LinuxDiskUsage:
    case defaultAgentConditionUids.DiskFreeSpace:
    case defaultConditionUids.DataStoreFreeSpace:
    case defaultConditionUids.CheckpointSize:
    case defaultConditionUids.SnapshotSize:
      if (parameters.unit === "PERCENT") {
        name = parameters.thresholdPercent
      }
      if (parameters.unit === "BYTES") {
        name = parameters.thresholdBytes
      }
      conditionName =
        localized(getLocalizationStringFor(conditionUid)) +
        " " +
        localized("editors.policy.conditions.is") +
        " " +
        operatorOptionsKeys[parameters.operator]() +
        " " +
        name +
        " "

      if (unit === "BYTES") {
        conditionName += conditionAbrevToWord[parameters.unitBytes] + "Byte "
      }
      if (unit === "PERCENT") {
        conditionName += " % "
      }

      if (parameters.duration) {
        conditionName +=
          localized("editors.policy.conditions.for") +
          " " +
          parameters.duration +
          " " +
          localized("general.minutes").toLowerCase()
      }

      if (parameters.excludeBoot) {
        conditionName += " " + localized("editors.policy.conditions.excludeBootVolume")
      }

      if (parameters.excludeRecovery) {
        conditionName += " " + localized("editors.policy.conditions.excludeRecoveryWin")
      }

      if (parameters.excludeRemovableDisk) {
        conditionName += " " + localized("editors.policy.conditions.excludeRemovableWin")
      }

      return conditionName
    case defaultConditionUids.VMHostUptime:
      return (
        localized("editors.policy.conditions.vmHostIsUp") +
        " " +
        localized("editors.policy.conditions.for") +
        " " +
        parameters.days +
        " " +
        localized("editors.policy.conditions.days")
      )
    case defaultConditionUids.BadSensorCount:
      return (
        localized(getLocalizationStringFor(conditionUid)) +
        " " +
        localized("editors.policy.conditions.is") +
        " " +
        operatorOptionsKeys[parameters.operator]() +
        " " +
        parameters.threshold
      )
    case defaultConditionUids.DeviceDown:
    case defaultAgentConditionUids.DeviceDown:
      return (
        localized(getLocalizationStringFor(conditionUid)) +
        " " +
        localized("editors.policy.conditions.for") +
        " " +
        parameters.duration +
        " " +
        localized(getUnitType(parameters.unit)).toLowerCase()
      )
    case defaultConditionUids.SensorHealth:
      return (
        parameters.name.length +
        " " +
        localized("editors.policy.conditions.sensorsWith") +
        " " +
        getStatusString(parameters.state, stateKeysToObject) +
        " " +
        localized("general.state").toLowerCase()
      )
    case defaultConditionUids.ProcessorUsage:
      return (
        localized(getLocalizationStringFor(conditionUid)) +
        " " +
        localized("editors.policy.conditions.is") +
        " " +
        operatorOptionsKeys[parameters.operator]() +
        " " +
        parameters.threshold +
        " % " +
        localized("editors.policy.conditions.for") +
        " " +
        parameters.duration +
        " " +
        localized("general.minutes").toLowerCase()
      )
    case defaultConditionUids.GuestOperationMode:
      return (
        localized(getLocalizationStringFor(conditionUid)) +
        " " +
        localized("editors.policy.conditions.withNames") +
        " " +
        getStatusString(parameters.mode, modeKeysToObject)
      )
    case defaultConditionUids.CheckpointLifeSpan:
    case defaultConditionUids.SnapshotLifeSpan:
      return (
        localized(getLocalizationStringFor(conditionUid)) +
        " " +
        localized("editors.policy.conditions.is") +
        " " +
        operatorOptionsKeys[parameters.operator]() +
        " " +
        parameters.duration +
        " " +
        localized(getUnitType(parameters.unit))
      )
    case defaultConditionUids.GuestToolsNotRunning:
      return localized(getLocalizationStringFor(conditionUid))
    case defaultAgentConditionUids.NetworkUtilization:
      return `${localized(getLocalizationStringFor(conditionUid))} ${byteOptionsKeys[parameters.type]()} ${localized(
        "editors.policy.conditions.are",
      )} ${operatorOptionsKeys[parameters.operator]()} ${parameters.thresholdBytes} ${
        unitBpsOptionsKeys[parameters.unitBytes]
      } ${localized("editors.policy.conditions.for")} ${parameters.duration} ${localized(
        "general.minutes",
      ).toLowerCase()}`
    case defaultAgentConditionUids.DiskVolumeIO:
      // Disk active time
      if (parameters.unit === "PERCENT") {
        return (
          localized("editors.policy.conditions.diskActiveTime") +
          " " +
          operatorOptionsKeys[parameters.operator]() +
          " " +
          parameters.threshold +
          "% " +
          localized("editors.policy.conditions.for") +
          " " +
          parameters.duration +
          " " +
          localized("general.minutes").toLowerCase()
        )
      }

      // Disk transfer rate
      return `${localized("editors.policy.conditions.disk")} ${rwOptionsKeys[parameters.type]()} ${localized(
        "editors.policy.conditions.is",
      )} ${operatorOptionsKeys[parameters.operator]()} ${parameters.thresholdBytes} ${
        unitBpsOptionsKeys[parameters.unitBytes]
      } ${localized("editors.policy.conditions.for")} ${parameters.duration} ${localized(
        "general.minutes",
      ).toLowerCase()}`

    default:
      throw new Error("Invalid policy id")
  }
}

export const generatedDescription = condition => {
  let generatedDescription = ""

  const resetThresholdManual = condition.resetThreshold === 2147480000
  const hasResetIfNotPresentSeconds = condition.resetIfNotPresentSeconds
  const resetIfNotPresentSeconds = hasResetIfNotPresentSeconds && condition.resetIfNotPresentSeconds !== 0
  const resetIfNotPresentSecondsManual = hasResetIfNotPresentSeconds && condition.resetIfNotPresentSeconds === 0
  if (
    (resetThresholdManual && resetIfNotPresentSecondsManual) ||
    (!hasResetIfNotPresentSeconds && valueFromSeconds(condition.resetThreshold) > 24)
  ) {
    generatedDescription += localized("general.manual")
  } else {
    if (!resetThresholdManual) {
      generatedDescription +=
        localized("editors.policy.conditions.resetIn") +
        " " +
        valueFromSeconds(condition.resetThreshold) +
        " " +
        localized("general.hours")
      if (resetIfNotPresentSeconds) {
        generatedDescription += " " + localized("general.and")
      }
    }
    if (resetIfNotPresentSeconds) {
      if (resetThresholdManual) {
        generatedDescription += localized("general.reset")
      }
      if (condition.resetIfNotPresentSeconds === 1) {
        generatedDescription += " " + localized("editors.policy.conditions.whenNoLongerMet")
      } else {
        generatedDescription +=
          " " +
          localized("editors.policy.conditions.whenNotMetFor") +
          " " +
          valueFromSeconds(condition.resetIfNotPresentSeconds) +
          " " +
          localized("general.hours")
      }
    }
  }
  return generatedDescription
}

export const generateConditionForServer = (conditionData, nodeRole) => {
  let data = pick(
    [
      "enabled",
      "displayName",
      "channels",
      "conditionName",
      "conditionUid",
      "parameters",
      "scripts",
      "inheritance",
      "ticketingAction",
      "ticketTemplateId",
      "ticketRulesetId",
      "createNinjaTicket",
      "severity",
      "priority",
      "notificationAction",
    ],
    conditionData,
  )

  let allowNotifications = conditionData.notificationAction !== "NONE"

  let resetThreshold = 2147480000
  let resetIfNotPresentSeconds = 0
  const resetAfterChecked = conditionData.resetAfterChecked
  const resetWhenConditionNotMetForChecked = conditionData.resetIfNotMetForChecked

  if (resetAfterChecked) {
    resetThreshold = valueToSeconds(conditionData.resetAfter)
  }
  if (resetWhenConditionNotMetForChecked) {
    resetIfNotPresentSeconds = valueToSeconds(conditionData.resetIfNotPresent)
  }

  data.resetThreshold = resetThreshold
  data.resetIfNotPresentSeconds = resetIfNotPresentSeconds
  data.notifyOnReset =
    allowNotifications && (resetAfterChecked || resetWhenConditionNotMetForChecked)
      ? conditionData.notifyOnReset
      : false

  return data
}

export const resetStatuses = condition => {
  if (isEmpty(condition)) return {}

  const resetThresholdManual = condition.resetThreshold === 2147480000
  const resetThreshold = valueFromSeconds(condition.resetThreshold)
  const resetAfterChecked = !resetThresholdManual
  const resetIfNotMetForChecked = Boolean(
    condition.resetIfNotPresentSeconds && condition.resetIfNotPresentSeconds !== 1,
  )
  const resetAfter = resetThreshold
  const resetIfNotPresent =
    valueFromSeconds(condition.resetIfNotPresentSeconds) || ResetThresholdOptionsWithoutManual.value

  return {
    resetThreshold,
    resetAfterChecked,
    resetIfNotMetForChecked,
    resetAfter,
    resetIfNotPresent,
  }
}

export const getAdditionalFields = (data, conditionUid) => {
  switch (conditionUid) {
    case defaultConditionUids.Memory:
    case defaultConditionUids.DiskUsage:
    case defaultAgentConditionUids.DiskUsage:
    case defaultAgentConditionUids.MacDiskUsage:
    case defaultAgentConditionUids.DiskFreeSpace:
    case defaultConditionUids.DataStoreFreeSpace:
    case defaultConditionUids.CheckpointSize:
    case defaultConditionUids.SnapshotSize:
    case defaultAgentConditionUids.DiskVolumeIO:
    case defaultAgentConditionUids.NetworkUtilization:
      const { unit, threshold } = data
      let thresholdPercent = 80
      let thresholdBytes = 1
      let unitBytes = "GB"
      if (unit === "BYTES") {
        const { prefix, value } = convertBytes(threshold)
        thresholdBytes = value
        unitBytes = prefix
      } else {
        thresholdPercent = threshold
      }
      return {
        thresholdPercent,
        thresholdBytes,
        unitBytes,
      }
    default:
      return {}
  }
}

export const keysFromSeleted = selected => {
  const selectedValue = Array.isArray(selected) ? selected : [selected]
  return map(obj => obj.value, selectedValue)
}

export const arrayFromKeys = (keys, obj) => {
  return map(key => {
    return { ...obj[key], label: obj[key].label() }
  }, keys)
}

export const getPathToCondition = conditionUid => {
  return `conditions.${conditionUid}`
}

export const getCompletePathToItem = pathToItem => ["content", ...pathToItem.split(".")]
